import { ChildRef } from '../../../../../lib/web/components/child-ref';
import { ChildrenRef } from '../../../../../lib/web/components/children-ref';
import { ClickListener } from '../../../../../lib/web/components/click-listener';
import { Component } from '../../../../../lib/web/components/component';
import { ComponentBase } from '../../../../../lib/web/components/component-base';
import { KeyDownListener } from '../../../../../lib/web/components/key-down-listener';
import { Key } from '../../../../../lib/web/core/key';
import { MouseEnterListener } from '../../../../../lib/web/components/mouse-enter-listener';
import { MouseLeaveListener } from '../../../../../lib/web/components/mouse-leave-listener';
import { SessionService } from '../../../services/session.service';
import { UserWatchingHistoryService } from '../../../services/user-watching-history.service';
import { ApiService } from '../../../services/api.service';
import { MediaPlayerService, MediaType } from '../../../services/media-player.service';
import { Str } from '../../../../../lib/web/core/str';
import { SessionStorageService } from '../../../../../lib/web/services/session-storage.service';
import { CardNavigationService } from '../../../services/card-navigation.service';
import { DataEventService } from '../../../services/data-event.service';

@Component({
    selector: '.o-slider'
})
export class SliderComponent extends ComponentBase<HTMLElement> {

    private _intervalTimer: NodeJS.Timeout | null = null;
    private _touchX: number = null;
    private _touchY: number = null;
    private _touchDirection: 'next' | 'previous' = null;
    private _isMouseEnter: boolean = false;
    private _isTouchMoving: boolean = false;
    private _defaultInterval: number = 5000;
    
    private readonly _tabindexRestoreSentinel: string = '__null__';

    @ChildrenRef(HTMLDivElement, '.o-slider__slide')
    private _slideElements: HTMLDivElement[] = null;
    @ChildrenRef(HTMLButtonElement, '.o-slider__bullet')
    private _bulletElements: HTMLButtonElement[] = null;
    @ChildRef('.o-slider__item-arrow--start')
    private _startArrowElement: HTMLDivElement = null;
    @ChildRef('.o-slider__item-arrow--end')
    private _endArrowElement: HTMLDivElement = null; 
    
    public constructor(node: HTMLElement, 
        private _sessionStorageService: SessionStorageService,
        private _sessionService: SessionService,
        private _apiService: ApiService,
        private _userWatchingHistoryService: UserWatchingHistoryService,
        private _mediaPlayerService: MediaPlayerService,
        private _cardNavigationService: CardNavigationService,
        private _dataEventService: DataEventService) {
        super(node);
    }

    public onInit(): void {
        this.slide(0);
        this.startSlideInterval();
        this._bulletElements.forEach(b => b.addEventListener('click', () => this.slide(parseInt(b.dataset.index), true)));
        this._slideElements.forEach((b, index) => {
            b.dataset.index = index.toString();
            b.addEventListener('touchstart', e => this.onTouchStart(e));
            b.addEventListener('touchmove', e => this.onTouchMove(e));
            b.addEventListener('touchend', e => this.onTouchEnd(e));
            b.addEventListener('transitionend', e => this.onSlideTransitionEnd(e));
            b.addEventListener('focusin', () => {
                this.clearInterval();
            });
            b.addEventListener('focusout', (e: FocusEvent) => {
                if (!this._node.contains(e.relatedTarget as Node)) {
                    this.startSlideInterval();
                }
            });
        });
        this.checkUserWatchingHistory();
        this._cardNavigationService.enableRowNavigation(this._node);
    }

    @ClickListener('a')
    public onViewContent(event: MouseEvent): void {
        const item: HTMLAnchorElement = event.currentTarget as HTMLAnchorElement;        
        this._dataEventService.viewContent(new URL(item.href).pathname);
    }
    
    @MouseEnterListener()
    public onMouseEnter(): void {
        this._isMouseEnter = true;
        this.setArrows();
    }

    @MouseLeaveListener()
    public onMouseOut(): void {
        this._isMouseEnter = false;          
        this._endArrowElement.style.display = 'none';      
        this._startArrowElement.style.display = 'none';
    }

    @ClickListener('.o-slider__item-arrow--start')
    public onStartArrowClick(): void {
        this.slide('previous', true);
    }

    @ClickListener('.o-slider__item-arrow--end')
    public onEndArrowClick(): void {
        this.slide('next', true);
    }

    @ClickListener('.o-slider__play')
    public onPlayClick(e: MouseEvent): void {
        this.playContent((e.target as HTMLElement).closest('.o-slider__slide') as HTMLDivElement);
    }

    private slide(direction?: 'next' | 'previous' | number, force?: boolean): void {
        const index: number = typeof direction === 'number' ? direction: direction == 'next' ? this.nextIndex: this.previousIndex;
        if (force) {
            this.clearInterval();
        }
        const outgoing: HTMLDivElement | undefined = this._slideElements.find(s => s.dataset.active === 'true');
        const slide: HTMLDivElement = this._slideElements[index];
        if (outgoing === slide) {
            return;
        }
        const slideDirection: 'next' | 'previous' = typeof direction === 'number'
            ? (index > parseInt(outgoing?.dataset.index ?? '0') ? 'next' : 'previous')
            : direction;
        this.animateSlides(outgoing, slide, slideDirection);

        this._slideElements.forEach((s, i) => {
            s.setAttribute('tabindex', '-1');
            s.setAttribute('aria-hidden', i === index ? 'false' : 'true');
        });

        this.updateSlidesFocusableTabIndex(index);

        this._bulletElements.forEach(b => b.setAttribute('tabindex', '-1'));

        this._bulletElements.forEach(s => s.dataset.active = 'false');
        const bullet: HTMLButtonElement = this._bulletElements[index];
        bullet.dataset.active = 'true';
        if (this._isMouseEnter)  {
            this.setArrows();
        }
    }

    private animateSlides(outgoing: HTMLDivElement | undefined, incoming: HTMLDivElement, direction: 'next' | 'previous'): void {
        const positionClasses: string[] = [
            'o-slider__slide--animating',
            'o-slider__slide--from-next',
            'o-slider__slide--from-previous',
            'o-slider__slide--to-next',
            'o-slider__slide--to-previous'
        ];
        this._slideElements
            .filter(s => s !== outgoing && s !== incoming)
            .forEach(s => {
                s.classList.remove(...positionClasses);
                s.dataset.active = 'false';
            });

        if (!outgoing) {
            incoming.dataset.active = 'true';
            return;
        }

        // Colocar el slide entrante en su lado de partida sin transición
        incoming.classList.remove(...positionClasses);
        incoming.classList.add(direction === 'next' ? 'o-slider__slide--from-next' : 'o-slider__slide--from-previous');
        void incoming.offsetWidth;

        incoming.classList.remove('o-slider__slide--from-next', 'o-slider__slide--from-previous');
        incoming.classList.add('o-slider__slide--animating');
        incoming.dataset.active = 'true';

        outgoing.classList.remove(...positionClasses);
        outgoing.classList.add('o-slider__slide--animating', direction === 'next' ? 'o-slider__slide--to-previous' : 'o-slider__slide--to-next');
        outgoing.dataset.active = 'false';
    }

    private onSlideTransitionEnd(event: TransitionEvent): void {
        const slide: HTMLDivElement = event.currentTarget as HTMLDivElement;
        if (event.target !== slide || event.propertyName !== 'transform') {
            return;
        }
        slide.classList.remove('o-slider__slide--animating', 'o-slider__slide--to-next', 'o-slider__slide--to-previous');
    }

    private focusActiveSlideFirstFocusable(): void {
        const activeSlide: HTMLDivElement | undefined = this._slideElements?.[this.currentIndex];
        if (!activeSlide) {
            return;
        }

        const preferred: HTMLElement | null = (activeSlide.querySelector('.o-slider__play:not([disabled])') as HTMLElement) ||
            (activeSlide.querySelector('.o-slider__more-info') as HTMLElement);
        if (preferred) {
            preferred.focus();
            return;
        }

        const focusableSelector: string = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]';
        const first: HTMLElement | undefined = Array.from(activeSlide.querySelectorAll(focusableSelector))[0] as HTMLElement;
        if (first) {
            first.focus();
            return;
        }

        activeSlide.focus();
    }

    private updateSlidesFocusableTabIndex(activeIndex: number): void {
        const focusableSelector: string = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]';

        this._slideElements.forEach((slide, slideIndex) => {
            const isActive: boolean = slideIndex === activeIndex;
            const focusables: HTMLElement[] = Array.from(slide.querySelectorAll(focusableSelector)) as HTMLElement[];

            focusables.forEach(element => {
                if (isActive) {
                    const stored: string = element.dataset['sliderOrigTabindex'];
                    if (stored !== undefined) {
                        if (stored === this._tabindexRestoreSentinel) {
                            element.removeAttribute('tabindex');
                        }
                        else {
                            element.setAttribute('tabindex', stored);
                        }
                        delete element.dataset['sliderOrigTabindex'];
                    }
                    return;
                }

                if (element.dataset['sliderOrigTabindex'] === undefined) {
                    const original: string | null = element.getAttribute('tabindex');
                    element.dataset['sliderOrigTabindex'] = original ?? this._tabindexRestoreSentinel;
                }
                element.setAttribute('tabindex', '-1');
            });
        });
    }

    private onTouchStart(event: TouchEvent): void {
        const touch: Touch = event.touches ? event.touches[0] : null;
        if (touch) {
            this._touchX = touch.clientX;
            this._touchY = touch.clientY;
        }
    }

    private onTouchMove(event: TouchEvent): void {
        const touch: Touch = event.touches ? event.touches[0] : null;
        if (touch) {
            this._isTouchMoving = true;
            const deltaX: number = -1 * (touch.clientX - this._touchX);
            if (deltaX > 0) {
                this._touchDirection = 'next';
            } 
            else {
                this._touchDirection = 'previous';
            }
        }
    }

    private onTouchEnd(event: TouchEvent): void {
        if (this._isTouchMoving) {
            this._isTouchMoving = false;
            if (this._touchDirection == 'next') {
                this.slide('next', true)
            } 
            else if (this._touchDirection == 'previous') {
                this.slide('previous');
            }
        }
    }

    private setArrows(): void {
        if (!document.body.classList.contains('touch')) {
            if (this._slideElements.length > 1) {
                this._startArrowElement.style.display = 'flex';
                this._endArrowElement.style.display = 'flex';
            }
        }
    }

    private async playContent(item: HTMLDivElement): Promise<void> {
        let contentUrl: string = item.querySelector('.o-slider__more-info').getAttribute('href');
        const freePlay: boolean = item.dataset.contentFreePlay === 'true';
        if (this._sessionService.isSigned || freePlay) {
            let capsuleId: number = ['video', 'podcast'].includes(item.dataset.contentType) ? parseInt(item.dataset.contentId): null;
            const collectionId: number = item.dataset.contentType === 'collection' ? parseInt(item.dataset.contentId): null;
            const image: string = item.querySelector('.o-slider__image img').getAttribute('src');
            if (collectionId) {
                if (this._userWatchingHistoryService.isWatching(collectionId, 'collection')) {
                    capsuleId = this._userWatchingHistoryService.getItem(collectionId, 'collection').capsuleId;
                    const { isArchived } = this._userWatchingHistoryService.getItem(capsuleId, 'capsule');
                    contentUrl = await this._apiService.get(`/api/v1/collection/continue-url/${collectionId}/${capsuleId}/${isArchived}`);
                }
                else {
                    const { url, id } = await this._apiService.get(`/api/v1/collection/${collectionId}/first-capsule`);
                    contentUrl = url;
                    capsuleId = id;
                }
            }
            this.loadPlayer(false, 'capsule', {
                capsuleId,
                url: contentUrl,
                collectionId,
                image,
                playerType: item.dataset.playerType
            });
        } 
        else {
            this._sessionStorageService.set('redirect-after-sign-in', contentUrl);
            this.redirect('/u/sign-in');
        }
    }
    
    private async loadPlayer(pushState: boolean = true, type: MediaType = 'capsule', content?: any): Promise<void> {                
        this._mediaPlayerService.loadMedia(content.playerType, {
            capsuleId: type == 'capsule' ? content.capsuleId: null,
            collectionId: content.collectionId,
            type,
            image: content.image,
            owner: type == 'capsule' 
                ? {  pathname: content.url,
                    target: content.url
                }
                : {
                    pathname: location.pathname,
                    target: Str.getRelativeUrl(location.href)
                }        
        });        
    }

    private checkUserWatchingHistory(): void {
        const playButtons: HTMLButtonElement[] = Array.from(this._node.querySelectorAll('.o-slider__play'));
        playButtons.forEach(button => {
            const item: HTMLDivElement = button.closest('.o-slider__slide') as HTMLDivElement;
            const contentId: number = parseInt(item.dataset.contentId);
            const contentType: string = item.dataset.contentType;
            const contentMediaType: string = item.dataset.contentMediaType;
            if (this._userWatchingHistoryService.isWatching(contentId, contentType === 'collection' ? 'collection': 'capsule')) {
                button.querySelector('span').innerText = contentMediaType === 'video' ? USER_LOCALE.continueWatching: USER_LOCALE.continueListening;
            }
        });
    }

    private clearInterval(): void {
        if (this._intervalTimer) {
            clearInterval(this._intervalTimer);
            this._intervalTimer = null;
        }
    }

    private startSlideInterval(): void {
        this.clearInterval();
        this._intervalTimer = setInterval(() => this.slide('next'), this._defaultInterval);
    }

    public get currentIndex(): number {
        return parseInt(this._slideElements.find(s => s.dataset.active === 'true').dataset.index);
    }

    public get nextIndex(): number {
        const currentIndex: number = this.currentIndex;
        if (currentIndex < this._slideElements.length - 1) {
            return currentIndex + 1;
        }
        else {
            return 0;
        }
    }

    public get previousIndex(): number {
        const currentIndex: number = this.currentIndex;
        if (currentIndex > 0) {
            return currentIndex - 1;
        }
        else {
            return this._slideElements.length - 1;
        }
    }

    public dispose(): void {
        this._cardNavigationService.disableNavigation(this._node);
        super.dispose();
    }
}